import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Expand, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { PORTFOLIO_CATEGORIES } from "../data/siteData";
import { Reveal, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Portfolio = () => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        axios.get(`${API}/portfolio`).then((res) => setItems(res.data)).catch(() => {});
    }, []);

    const visible = filter === "all" ? items : items.filter((i) => i.category === filter);

    return (
        <section id="portfolio" data-testid="portfolio-section" className="py-16 sm:py-24 lg:py-32 bg-[#0F0F12] text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="max-w-2xl">
                    <p className="font-mono-label text-xs text-[#FFCC00] mb-4">Portfolio</p>
                    <h2 data-testid="portfolio-title" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
                        Lavori che parlano da soli
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-zinc-400 leading-relaxed">
                        Una selezione di progetti realizzati nel nostro laboratorio: clicca su un
                        lavoro per vederlo in grande.
                    </p>
                </Reveal>

                <Reveal delay={0.12} className="mt-10">
                    <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
                        {PORTFOLIO_CATEGORIES.map((cat) => (
                            <button
                                key={cat.id}
                                data-testid={cat.testid}
                                onClick={() => setFilter(cat.id)}
                                className={`text-xs font-mono-label px-4 py-2.5 border transition-colors duration-300 ${
                                    filter === cat.id
                                        ? "bg-[#FF0066] border-[#FF0066] text-white"
                                        : "border-zinc-700 text-zinc-400 hover:border-zinc-400 hover:text-white"
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="portfolio-grid">
                    <AnimatePresence mode="popLayout">
                        {visible.map((item, i) => {
                            const featured = filter === "all" && i === 0;
                            return (
                                <motion.button
                                    key={item.id}
                                    layout
                                    data-testid={`portfolio-card-${item.id}`}
                                    onClick={() => setSelected(item)}
                                    className={`portfolio-card group relative text-left border border-zinc-800 bg-[#18181C] overflow-hidden hover:border-zinc-500 transition-colors duration-300 ${featured ? "sm:col-span-2" : ""}`}
                                    initial={{ opacity: 0, scale: 0.94 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.94 }}
                                    transition={{ duration: 0.4, ease: EASE }}
                                >
                                    <div className={`relative overflow-hidden ${featured ? "aspect-[4/3] sm:aspect-[21/9]" : "aspect-[4/3]"}`}>
                                        <img src={item.image} alt={item.title} loading="lazy" className="w-full h-full object-cover" />
                                        <span className="absolute top-3 right-3 p-2 bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <Expand size={16} />
                                        </span>
                                    </div>
                                    <div className="p-5 min-h-[104px] flex flex-col justify-center">
                                        <span className="text-xs font-mono-label text-[#FFCC00]">{item.category_label}</span>
                                        <h3 className="mt-2 font-heading text-lg font-bold leading-snug">{item.title}</h3>
                                    </div>
                                </motion.button>
                            );
                        })}
                    </AnimatePresence>
                </div>
            </div>

            <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
                <DialogContent data-testid="lightbox-modal" className="max-w-4xl bg-[#0F0F12] border-zinc-800 text-white p-0 overflow-hidden">
                    {selected && (
                        <div>
                            <img src={selected.image} alt={selected.title} data-testid="lightbox-image" className="w-full max-h-[60vh] object-contain bg-black" />
                            <div className="p-6 sm:p-8">
                                <span className="text-xs font-mono-label text-[#FFCC00]">{selected.category_label}</span>
                                <h3 data-testid="lightbox-title" className="mt-2 font-heading text-xl sm:text-2xl font-bold">{selected.title}</h3>
                                <p data-testid="lightbox-description" className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">{selected.description}</p>
                                <a
                                    href="#contatti"
                                    data-testid="lightbox-cta"
                                    onClick={() => setSelected(null)}
                                    className="mt-6 inline-flex items-center gap-2 bg-[#FF0066] text-white text-sm font-semibold px-6 py-3 hover:bg-white hover:text-[#0E0E10] transition-colors duration-300"
                                >
                                    Richiedi un progetto simile <ArrowUpRight size={15} />
                                </a>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
};
